// @ts-ignore
import Navigo from "navigo";
// @ts-ignore
import { before, disable as disableXHook, enable as enableXHook } from "xhook";
import { APIHandler } from "./apiHandler";
import { defaultHandlers as configHandlers } from "./config/handlers";
import { defaultHandlers as nbConvertHandlers } from "./nbconvert/handlers";
import { FileManager } from "./contents/filemanager";
import { defaultHandlers as contentsHandlers } from "./contents/handlers";
import { defaultHandlers as buildHandlers } from "./build/handlers";
import { Context, setContext } from "./context";
import { defaultHandlers as nbdimeHandlers } from "./extensions/nbdime/handlers";
import { defaultHandlers as lspHandlers } from "./extensions/lsp/handlers";
import { defaultHandlers as jupyterlabHandlers } from "./jupyterlab/handlers";
import { defaultHandlers as kernelsHandlers } from "./kernels/handlers";
import { defaultHandlers as kernelspecsHandlers } from "./kernelspecs/handlers";
import { defaultHandlers as sessionsHandlers } from "./sessions/handlers";

const DUMMY_BASE_URL = "https://www.kaggle.com";

// Ensure xhook doesn't interfere with other XHR interception.
try {
  disableXHook();
} catch (err) {
  // Prevent unit tests from breaking when `disable` import is not a function.
  // eslint-disable-next-line no-console
  console.warn("Error occurred disabling xhook.", err.message);
}

// Registers handlers for all HTTP methods on '/static/dist/jupyterlab/api/.*'.
export const registerJupyterApiRoutes = () => {
  const BASE_URL = "/static/dist/jupyterlab";
  // Reset route to allow Navigo to resolve same URL multiple times in a row.
  const DUMMY_DUPE_ROUTE = "no-such-route";
  const API_ROUTE_REGEX = new RegExp(
    "(/metrics)|(/lsp/status)|(/lsp/ws/(pylsp|r-languageserver))|(/(lab|api|nbdime)/(api|config|contents|kernels|kernelspecs|nbconvert|security|sessions|terminals|settings)).*"
  );
  const ALL_HANDLERS = [
    ...contentsHandlers,
    ...kernelspecsHandlers,
    ...configHandlers,
    ...sessionsHandlers,
    ...kernelsHandlers,
    ...nbdimeHandlers,
    ...jupyterlabHandlers,
    ...lspHandlers,
    ...buildHandlers,
    ...nbConvertHandlers,
  ];

  // Enable xhook in editor frame for XHR interception.
  try {
    enableXHook();
  } catch (err) {
    // Prevent unit tests from breaking when `enable` import is not a function.

    // eslint-disable-next-line no-console
    console.warn("Error occurred enabling xhook.", err.message);
  }

  const context = setContext(new Context());

  // Handles URL routing and path/query string parameter extraction.
  const router = new Navigo();

  let binding: {
    params: any;
    handler: typeof APIHandler;
  } | null;

  for (const [url, handler] of ALL_HANDLERS) {
    // Navigo route handlers must be synchronous, so only capture bound params and handler.
    router.on(url, (params: any) => {
      binding = { params, handler };
    });
  }

  // eslint-disable-next-line no-empty,no-empty-function,@typescript-eslint/no-empty-function
  router.on(DUMMY_DUPE_ROUTE, () => {});

  // This function must remain synchronous and always set 'binding' to null on
  // exit to ensure correct behavior.
  const getHandlerBinding = (url: URL) => {
    try {
      let normalizedPath = `${url?.pathname.replace(BASE_URL, "")}${
        url?.search
      }`;

      if (context.jupyterProxyUrl) {
        const proxyPathname = new URL(context.jupyterProxyUrl).pathname;
        normalizedPath = normalizedPath.replace(proxyPathname, "");
      }

      context.normalizedPath = normalizedPath;
      router.resolve(normalizedPath);

      if (!binding) {
        // Proxy request to back end.
        return null;
      }

      return {
        handler: binding.handler,
        params: binding.params,
      };
    } finally {
      binding = null;
      // Clear out previous resolved path to allow duplicates.
      router.resolve(DUMMY_DUPE_ROUTE);
    }
  };

  const apiHandler = async (request: Request): Promise<Response | null> => {
    if (!request) {
      return null;
    }

    const handlerBinding = getHandlerBinding(
      // URL is just a convenience object to access path and search params.
      new URL(request.url, DUMMY_BASE_URL)
    );

    if (!handlerBinding) {
      // Proxy request to back end.
      return null;
    }

    const handler = new (handlerBinding.handler as any)(
      request,
      new FileManager()
    );

    const response = await handler.handle(handlerBinding.params);
    return response;
  };

  const buildHookResponse = async (response: Response) => {
    const headers: { [key: string]: string } = {};
    response.headers.forEach((value: string, key: string) => {
      headers[key] = value;
    });

    const hookResponse = {
      status: response.status,
      statusText: response.statusText,
      text: await response.text(),
      headers,
    };

    return hookResponse;
  };

  // WARNING: this was async/await and silently broke with swc
  // It's likely the fault of SWC, but don't revert without double
  // checking the editor
  before((request: Request, callback: any) => {
    context.request = request;

    if (!request.url.match(API_ROUTE_REGEX)) {
      callback();
      return;
    }

    apiHandler(request).then(response => {
      if (!response?.status) {
        throw new Error(
          `Handler not found for Jupyter API interception: ${request.method} - ${request.url}`
        );
      }

      buildHookResponse(response).then(hookResponse => {
        if (hookResponse.status >= 400) {
          throw new Error(
            `Unexpected result from Jupyter API interception: ${
              request.method
            } - ${request.url} ${JSON.stringify(hookResponse)}`
          );
        }

        callback(hookResponse);
      });
    });
  });
};
